import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Tooltip,
  Badge,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Lock, CheckCircle, RestartAlt, TrendingUp, LockClock, MenuOpen, Menu } from '@mui/icons-material';
import { TalentNode, TREE_CATEGORIES } from '../data/unifiedTalentTree';
import { unifiedTalentTreeService, TalentRecommendation } from '../services/unifiedTalentTreeService';
import { healthImpactService, HealthStats } from '../services/healthImpactService';
import { weeklyLockInService, WeeklyLockingWithTalents } from '../services/weeklyLockInService';
import { useThemeMode } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { withTimeout, debugLogForUser } from '../utils/asyncUtils';
import ZoomPanContainer from './ZoomPanContainer';

interface UnifiedTalentTreeViewProps {
  // Simplified interface - only explore mode now
}

const GRID_SIZE = 80; // Distance between grid points
const NODE_SIZE = 60; // Size of talent nodes

// Free user talents - only these 3 are available for free users
const FREE_USER_TALENTS = ['sleep-hygiene', 'stability-mastery', 'sunseeker'];

const TalentNodeButton = styled(Button, {
  shouldForwardProp: (prop) => typeof prop === 'string' && !prop.startsWith('$'),
})<{
  $unlocked: boolean;
  $hasSessions: boolean;
  $selected: boolean;
  $treeColor: string;
  $available: boolean;
  $isDark: boolean;
  $locked: boolean;
}>(({ $unlocked, $hasSessions, $selected, $treeColor, $available, $isDark, $locked }) => ({
  minWidth: `${NODE_SIZE}px`,
  width: `${NODE_SIZE}px`,
  height: `${NODE_SIZE}px`,
  borderRadius: '50%',
  padding: 0,
  border: `5px solid ${
    $locked ? '#666' :
    !$available ? '#666' :
    $hasSessions ? '#ffd700' : 
    $selected ? '#ff4444' : 
    $treeColor
  }`,
  backgroundColor: $isDark 
    ? ($locked ? '#1a1a1a' : !$available ? '#1a1a1a' : $unlocked ? '#000000' : '#0a0a0a')
    : ($locked ? '#e0e0e0' : !$available ? '#e0e0e0' : $unlocked ? '#ffffff' : '#f5f5f5'),
  transform: 'scale(1)',
  boxShadow: $locked 
    ? 'none'
    : !$available 
      ? 'none'
      : $selected 
        ? '0 0 20px #ff4444' 
        : ($unlocked || $available) ? `0 0 20px ${$hasSessions ? '#ffd700' : $treeColor}` : 'none',
  transition: 'all 0.3s ease',
  cursor: $locked ? 'not-allowed' : $available ? 'pointer' : 'not-allowed',
  opacity: $locked ? 0.5 : 1,
  position: 'relative',
  zIndex: 5,
  overflow: 'visible',
  
  '&:hover': {
    transform: 'scale(1)',
    backgroundColor: $locked 
      ? undefined
      : $available 
        ? ($isDark ? '#1a1a1a' : '#f0f0f0')
        : undefined,
    zIndex: 6,
  },
  
  '& .MuiSvgIcon-root': {
    fontSize: '54px',
    color: $locked 
      ? '#666'
      : !$available 
        ? '#666'
        : $hasSessions 
          ? '#ffd700' 
          : $unlocked 
            ? $treeColor 
            : ($isDark ? '#666' : '#9ca3af'),
    filter: $locked ? 'none' : $available && $unlocked ? 'drop-shadow(0 0 4px currentColor)' : 'none',
  },
  
  // Add a solid background disc to ensure no lines show through
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -5,
    left: -5,
    right: -5,
    bottom: -5,
    borderRadius: '50%',
    backgroundColor: $isDark ? '#000000' : '#ffffff',
    zIndex: -1,
  },
}));

// Create computed styles function
const getHealthTreeNodeStyles = (
  $unlocked: boolean,
  $hasSessions: boolean,
  $selected: boolean,
  $treeColor: string,
  $available: boolean,
  $isDark: boolean
) => ({
  // Button reset styles
  font: 'inherit',
  color: 'inherit',
  background: 'none',
  margin: 0,
  outline: 'none',
  textAlign: 'center' as const,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  verticalAlign: 'middle',
  
  // Custom styles
  minWidth: `${NODE_SIZE}px`,
  width: `${NODE_SIZE}px`,
  height: `${NODE_SIZE}px`,
  borderRadius: '0',
  padding: 0,
  border: `5px solid ${
    !$available ? '#666' :
    $hasSessions ? '#ffd700' : 
    $selected ? '#ff4444' : 
    $treeColor
  }`,
  backgroundColor: $isDark 
    ? (!$available ? '#1a1a1a' : $unlocked ? '#000000' : '#0a0a0a')
    : (!$available ? '#e0e0e0' : $unlocked ? '#ffffff' : '#f5f5f5'),
  transform: 'scale(1)',
  filter: !$available 
    ? 'none'
    : $selected 
      ? 'drop-shadow(0 0 20px #ff4444)' 
      : ($unlocked || $available) ? `drop-shadow(0 0 20px ${$hasSessions ? '#ffd700' : $treeColor})` : 'none',
  transition: 'all 0.3s ease',
  cursor: $available ? 'pointer' : 'not-allowed',
  opacity: 1,
  position: 'relative' as const,
  zIndex: 5,
  overflow: 'visible',
  clipPath: 'polygon(18% 0%, 82% 0%, 100% 18%, 100% 82%, 82% 100%, 18% 100%, 0% 82%, 0% 18%)',
  
  '&:hover': {
    transform: 'scale(1)',
    backgroundColor: $available 
      ? ($isDark ? '#1a1a1a' : '#f0f0f0')
      : undefined,
    zIndex: 6,
    '& img': {
      filter: 'drop-shadow(0 0 4px currentColor) brightness(1.3)',
    },
  },
  
  '& .MuiSvgIcon-root': {
    fontSize: '54px',
    color: !$available 
      ? '#666'
      : $hasSessions 
        ? '#ffd700' 
        : $unlocked 
          ? $treeColor 
          : ($isDark ? '#666' : '#9ca3af'),
    filter: $available && $unlocked ? 'drop-shadow(0 0 4px currentColor)' : 'none',
  },
  
  // Add a solid background disc to ensure no lines show through
  '&::before': {
    content: '""',
    position: 'absolute' as const,
    top: -5,
    left: -5,
    right: -5,
    bottom: -5,
    borderRadius: '0',
    backgroundColor: $isDark ? '#000000' : '#ffffff',
    zIndex: -1,
    clipPath: 'polygon(18% 0%, 82% 0%, 100% 18%, 100% 82%, 82% 100%, 18% 100%, 0% 82%, 0% 18%)',
  },
  
});

// Simple styled component without $ props
const HealthTreeNodeButtonStyled = styled('button')({});

// Wrapper component to filter out $ props
const HealthTreeNodeButtonWrapper: React.FC<{
  $unlocked: boolean;
  $hasSessions: boolean;
  $selected: boolean;
  $treeColor: string;
  $available: boolean;
  $isDark: boolean;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  disabled?: boolean;
}> = ({ children, onClick, onContextMenu, disabled, $unlocked, $hasSessions, $selected, $treeColor, $available, $isDark }) => {
  const styles = getHealthTreeNodeStyles($unlocked, $hasSessions, $selected, $treeColor, $available, $isDark);
  
  return (
    <HealthTreeNodeButtonStyled
      style={styles}
      onClick={onClick}
      onContextMenu={onContextMenu}
      disabled={disabled}
    >
      {children}
    </HealthTreeNodeButtonStyled>
  );
};

const UnifiedTalentTreeView: React.FC<UnifiedTalentTreeViewProps> = () => {
  const { mode: themeMode } = useThemeMode();
  const { user } = useAuth();
  const { isPremium } = useSubscription();
  const navigate = useNavigate();
  const [talents, setTalents] = useState<TalentNode[]>([]);
  const [recommendations, setRecommendations] = useState<TalentRecommendation[]>([]);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [lockInConfirmationOpen, setLockInConfirmationOpen] = useState(false);
  const [activeLocking, setActiveLocking] = useState<WeeklyLockingWithTalents | null>(null);
  const [checkingLockIn, setCheckingLockIn] = useState(true);
  const [healthStats, setHealthStats] = useState<HealthStats>({ 
    totalYearsGained: 0, 
    healthWorthIncrease: 0, 
    longevityYears: 0, 
    healthspanYears: 0, 
    wellnessYears: 0, 
    overallHealthScore: 0,
    totalCostYears: 0
  });
  const [weeklyHours, setWeeklyHours] = useState<number>(6);
  const [showRecommendations, setShowRecommendations] = useState<boolean>(false);
  const [currentCommitmentMinutes, setCurrentCommitmentMinutes] = useState<number>(0);
  const [showControlPanel, setShowControlPanel] = useState<boolean>(true);
  const [selectedTalentId, setSelectedTalentId] = useState<string | null>(null);
  const [lockInCheckComplete, setLockInCheckComplete] = useState<boolean>(false);

  // Load talent tree and calculate health stats
  useEffect(() => {
    const loadedTalents = unifiedTalentTreeService.getTree();
    
    // Auto-click Health Tree if it has no sessions
    const healthTree = loadedTalents.find(t => t.id === 'health-tree');
    if (healthTree && healthTree.currentSession === 0) {
      unifiedTalentTreeService.upgradeTalent('health-tree');
      // Reload talents after auto-clicking
      const updatedTalents = unifiedTalentTreeService.getTree();
      setTalents(updatedTalents);
    } else {
      setTalents(loadedTalents);
    }
    
    // Calculate health statistics with user age
    const stats = healthImpactService.calculateHealthStats(loadedTalents, user?.age);
    setHealthStats(stats);
    
    // Calculate current weekly commitment in minutes
    const totalMinutes = loadedTalents.reduce((total, talent) => {
      if (talent.currentSession > 0) {
        return total + (talent.timeRequired * talent.currentSession);
      }
      return total;
    }, 0);
    setCurrentCommitmentMinutes(totalMinutes);
  }, [user?.age]); // Re-calculate when user age changes

  // Check for active weekly lock-in with timeout protection - only once per user
  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout;

    const checkActiveLockIn = async () => {
      // Only check if we haven't already completed this check for this user
      if (!user?.id || !mounted || lockInCheckComplete) return;
      
      try {
        setCheckingLockIn(true);
        debugLogForUser(user.email || '', 'checkActiveLockIn:start', { userId: user.id });
        
        // Add timeout protection to prevent infinite loading
        const lockingPromise = weeklyLockInService.getCurrentLocking(user.id);
        const currentLocking = await withTimeout(lockingPromise, 10000, 'checkActiveLockIn');
        
        if (mounted) {
          setActiveLocking(currentLocking);
          setLockInCheckComplete(true); // Mark check as complete
          debugLogForUser(user.email || '', 'checkActiveLockIn:success', { 
            hasLocking: !!currentLocking,
            lockingId: currentLocking?.id 
          });
        }
      } catch (error) {
        console.error('Error checking active lock-in:', error);
        debugLogForUser(user.email || '', 'checkActiveLockIn:error', error);
        
        if (mounted) {
          // Set to null instead of keeping loading state
          setActiveLocking(null);
          setLockInCheckComplete(true); // Mark check as complete even on error
          
          if (error instanceof Error && error.name === 'TimeoutError') {
            console.warn('checkActiveLockIn timed out, continuing without lock-in data');
            toast.error('Unable to load lock-in status. Please refresh the page if needed.');
          }
        }
      } finally {
        if (mounted) {
          setCheckingLockIn(false);
        }
      }
    };

    // Add a safety timeout to always clear loading state
    timeoutId = setTimeout(() => {
      if (mounted) {
        console.warn('Force clearing checkingLockIn after 15 seconds');
        setCheckingLockIn(false);
        setActiveLocking(null);
        setLockInCheckComplete(true); // Mark as complete to prevent retries
        debugLogForUser(user?.email || '', 'checkActiveLockIn:force_timeout');
      }
    }, 15000);

    checkActiveLockIn();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [user?.id, user?.email, lockInCheckComplete]);

  // Reset lock-in check state when user changes
  useEffect(() => {
    setLockInCheckComplete(false);
    setActiveLocking(null);
    setCheckingLockIn(false);
  }, [user?.id]);

  // Generate recommendations when requested
  useEffect(() => {
    if (showRecommendations) {
      const weeklyMinutes = weeklyHours * 60;
      const recs = unifiedTalentTreeService.getRecommendations(weeklyMinutes);
      setRecommendations(recs);
    } else {
      setRecommendations([]);
    }
  }, [weeklyHours, showRecommendations]);

  // Handle recommendation button click - do everything in one go
  const handleGetRecommendations = () => {
    // Auto-click Health Tree if it has no sessions before getting recommendations
    const currentTalents = unifiedTalentTreeService.getTree();
    const healthTree = currentTalents.find(t => t.id === 'health-tree');
    if (healthTree && healthTree.currentSession === 0) {
      unifiedTalentTreeService.upgradeTalent('health-tree');
    }
    
    const weeklyMinutes = weeklyHours * 60;
    let iterationCount = 0;
    const maxIterations = 20; // Safety limit to prevent infinite loops
    
    console.log(`Starting recommendations with ${weeklyMinutes} minutes budget`);
    
    // Keep recommending and applying until no more recommendations or time runs out
    while (iterationCount < maxIterations) {
      iterationCount++;
      console.log(`--- Recommendation iteration ${iterationCount} ---`);
      
      // Get current state
      let currentTalents = unifiedTalentTreeService.getTree();
      const currentTotalMinutes = currentTalents.reduce((total, talent) => {
        return total + (talent.currentSession * talent.timeRequired);
      }, 0);
      
      // Check if we've used up the time budget
      if (currentTotalMinutes >= weeklyMinutes) {
        console.log(`Time budget exhausted: ${currentTotalMinutes}/${weeklyMinutes} minutes used`);
        break;
      }
      
      // Get recommendations for remaining time
      const remainingMinutes = weeklyMinutes - currentTotalMinutes;
      const recs = unifiedTalentTreeService.getRecommendations(remainingMinutes);
      
      // If no more recommendations, we're done
      if (recs.length === 0) {
        console.log('No more recommendations available');
        break;
      }
      
      console.log(`Got ${recs.length} recommendations for ${remainingMinutes} remaining minutes`);
      
      // Apply all recommendations
      let appliedAny = false;
      recs.forEach(rec => {
        if (unifiedTalentTreeService.canSelectForCommitment(rec.talent.id)) {
          const currentSessions = rec.talent.currentSession;
          const targetSessions = currentSessions + rec.suggestedSessions;
          
          // Upgrade to target sessions
          for (let i = currentSessions; i < targetSessions; i++) {
            const success = unifiedTalentTreeService.upgradeTalent(rec.talent.id);
            if (success) {
              appliedAny = true;
              console.log(`Applied session to ${rec.talent.name} (${i + 1}/${targetSessions})`);
            }
          }
        }
      });
      
      // If we couldn't apply any recommendations, we're done
      if (!appliedAny) {
        console.log('Could not apply any more recommendations');
        break;
      }
    }
    
    if (iterationCount >= maxIterations) {
      console.warn('Reached maximum iteration limit for recommendations');
    }
    
    // Final update of everything
    const finalTalents = unifiedTalentTreeService.getTree();
    setTalents(finalTalents);
    
    // Update health statistics with user age
    const stats = healthImpactService.calculateHealthStats(finalTalents, user?.age);
    setHealthStats(stats);
    
    // Update current weekly commitment
    const totalMinutes = finalTalents.reduce((total, talent) => {
      if (talent.currentSession > 0) {
        return total + (talent.timeRequired * talent.currentSession);
      }
      return total;
    }, 0);
    setCurrentCommitmentMinutes(totalMinutes);
    
    // Show final recommendations for UI
    const finalRecs = unifiedTalentTreeService.getRecommendations(0); // Just for display
    setRecommendations(finalRecs);
    
    console.log(`Recommendations complete: ${totalMinutes}/${weeklyMinutes} minutes used across ${finalTalents.filter(t => t.currentSession > 0).length} talents`);
  };

  // Handle lock-in button click
  const handleLockInClick = () => {
    const talentsWithSessions = talents.filter(t => t.currentSession > 0 && t.id !== 'health-tree');
    
    if (talentsWithSessions.length < 3) {
      toast.error('You must have at least 3 talents with sessions to make a weekly commitment.');
      return;
    }
    
    setLockInConfirmationOpen(true);
  };

  // Confirm lock-in
  const confirmLockIn = async () => {
    if (!user?.id) return;
    
    try {
      const talentsWithSessions = talents.filter(t => t.currentSession > 0 && t.id !== 'health-tree');
      
      const talentSelections = talentsWithSessions.map(talent => ({
        talent_id: talent.id,
        talent_name: talent.name,
        target_minutes_per_week: talent.currentSession * talent.timeRequired,
        tree_type: talent.category as 'longevity' | 'healthspan' | 'wellness'
      }));
      
      await weeklyLockInService.createLocking(user.id, talentSelections);
      toast.success('Weekly commitment created successfully!');
      setLockInConfirmationOpen(false);
      navigate('/weekly-lockin');
    } catch (error) {
      console.error('Error creating lock-in:', error);
      toast.error('Failed to create weekly commitment. Please try again.');
    }
  };

  // Get lock-in button text and state
  const getLockInButtonState = () => {
    // Only show "Checking..." during initial load, not after we've already checked
    if (checkingLockIn && !lockInCheckComplete) {
      return { text: 'Checking...', disabled: true, icon: <LockClock /> };
    }
    
    if (activeLocking) {
      return { text: 'Weekly commitment completed', disabled: true, icon: <CheckCircle /> };
    }
    
    const talentsWithSessions = talents.filter(t => t.currentSession > 0 && t.id !== 'health-tree');
    const hasEnoughTalents = talentsWithSessions.length >= 3;
    
    return {
      text: 'Make a commitment for the week',
      disabled: !hasEnoughTalents,
      icon: <LockClock />
    };
  };

  // Calculate grid dimensions
  const gridDimensions = useMemo(() => {
    if (talents.length === 0) return { width: 800, height: 600, offsetX: 0, offsetY: 0 };
    
    const maxRow = Math.max(...talents.map(t => t.row));
    const maxCol = Math.max(...talents.map(t => t.col));
    const minRow = Math.min(...talents.map(t => t.row));
    const minCol = Math.min(...talents.map(t => t.col));
    
    return {
      width: (maxCol - minCol + 2) * GRID_SIZE,
      height: (maxRow - minRow + 2) * GRID_SIZE,
      offsetX: -minCol * GRID_SIZE + GRID_SIZE,
      offsetY: -minRow * GRID_SIZE + GRID_SIZE
    };
  }, [talents]);

  // Handle talent interaction (explore mode only)
  // Helper function to get image style based on locked/theme state
  const getImageStyle = (isLocked: boolean) => ({
    width: '60px', 
    height: '60px',
    borderRadius: '50%',
    clipPath: 'circle(50%)',
    filter: isLocked 
      ? 'grayscale(100%) brightness(0.7)' 
      : themeMode === 'dark' 
        ? 'drop-shadow(0 0 4px currentColor) invert(1) brightness(0.9) contrast(1.2)'
        : 'drop-shadow(0 0 4px currentColor) contrast(1.2) brightness(1.1)',
    backgroundColor: 'transparent',
    objectFit: 'cover' as const,
    opacity: isLocked ? 0.6 : 1
  });

  const handleTalentClick = (talent: TalentNode, event: React.MouseEvent) => {
    event.preventDefault();
    
    // Track selection state
    const newSelectedId = talent.id === selectedTalentId ? null : talent.id;
    console.log('Talent clicked:', talent.id, 'Current selected:', selectedTalentId, 'New selected:', newSelectedId);
    setSelectedTalentId(newSelectedId);
    
    // Check if talent is locked for free users
    const isLocked = !isPremium && talent.id !== 'health-tree' && !FREE_USER_TALENTS.includes(talent.id);
    if (isLocked) {
      toast.error('This talent is only available for Premium users. Upgrade to unlock all talents!');
      return;
    }
    
    // Check if talent is available for interaction
    const isAvailable = isTalentAvailable(talent);
    if (!isAvailable) {
      return; // Do nothing if talent is not available
    }
    
    // Explore mode - upgrade/downgrade talents
    if (event.button === 0 || event.type === 'click') {
      // Left click - upgrade
      if (talent.unlocked && talent.currentSession < talent.maxSession) {
        unifiedTalentTreeService.upgradeTalent(talent.id);
      }
    } else if (event.button === 2 || event.type === 'contextmenu') {
      // Right click - downgrade
      if (talent.currentSession > 0) {
        unifiedTalentTreeService.downgradeTalent(talent.id);
      }
    }
    
    // Refresh talents and recalculate health stats
    const updatedTalents = unifiedTalentTreeService.getTree();
    setTalents(updatedTalents);
    
    // Update health statistics with user age
    const stats = healthImpactService.calculateHealthStats(updatedTalents, user?.age);
    setHealthStats(stats);
    
    // Update current weekly commitment
    const totalMinutes = updatedTalents.reduce((total, talent) => {
      if (talent.currentSession > 0) {
        return total + (talent.timeRequired * talent.currentSession);
      }
      return total;
    }, 0);
    setCurrentCommitmentMinutes(totalMinutes);
  };

  // Check if talent is available for interaction (explore mode only)
  const isTalentAvailable = (talent: TalentNode): boolean => {
    // Debug logging for functional-mobility
    if (talent.id === 'functional-mobility') {
      console.log('=== FUNCTIONAL MOBILITY DEBUG ===');
      console.log('Talent:', talent);
      console.log('Requires:', talent.requires);
      console.log('Unlocked:', talent.unlocked);
      console.log('CurrentSession:', talent.currentSession);
      console.log('MaxSession:', talent.maxSession);
    }
    
    // First check if prerequisites are met with actual point investment
    if (talent.requires && talent.requires.length > 0) {
      let prereqsMet: boolean;
      
      // Handle both simple array (AND) and array of arrays (OR groups)
      if (Array.isArray(talent.requires[0])) {
        // OR condition: at least one group must be satisfied
        prereqsMet = (talent.requires as string[][]).some(group => 
          group.every(reqId => {
            const reqTalent = talents.find(t => t.id === reqId);
            if (!reqTalent) return false;
            
            // All prerequisites need actual points invested
            return reqTalent.currentSession > 0;
          })
        );
      } else {
        // AND condition: all requirements must be satisfied
        prereqsMet = (talent.requires as string[]).every(reqId => {
          const reqTalent = talents.find(t => t.id === reqId);
          if (!reqTalent) {
            if (talent.id === 'functional-mobility') {
              console.log('Required talent not found:', reqId);
            }
            return false;
          }
          
          // All prerequisites need actual points invested
          const hasPoints = reqTalent.currentSession > 0;
          if (talent.id === 'functional-mobility') {
            console.log(`Prereq ${reqId}: currentSession=${reqTalent.currentSession}, hasPoints=${hasPoints}`);
          }
          return hasPoints;
        });
      }
      
      if (talent.id === 'functional-mobility') {
        console.log('Prerequisites met:', prereqsMet);
      }
      
      if (!prereqsMet) return false;
    }
    
    // Then check if talent is unlocked and can be interacted with:
    // 1. Can be upgraded (unlocked and currentSession < maxSession), OR
    // 2. Can be downgraded (has sessions > 0)
    const isInteractable = talent.unlocked && (talent.currentSession < talent.maxSession || talent.currentSession > 0);
    
    if (talent.id === 'functional-mobility') {
      console.log('Can be interacted with:', isInteractable);
      console.log('Final availability:', isInteractable);
      console.log('=== END FUNCTIONAL MOBILITY DEBUG ===');
    }
    
    return isInteractable;
  };

  // Handle reset confirmation
  const handleResetConfirm = () => {
    unifiedTalentTreeService.resetAllProgress();
    const resetTalents = unifiedTalentTreeService.getTree();
    setTalents(resetTalents);
    setResetDialogOpen(false);
    
    // Reset health statistics with user age
    const stats = healthImpactService.calculateHealthStats(resetTalents, user?.age);
    setHealthStats(stats);
    
    // Reset current weekly commitment
    setCurrentCommitmentMinutes(0);
    
    // Show success message
    toast.success('Health Tree reset successfully! All talent progress has been cleared.');
    console.log('Tree reset completed. New state:', resetTalents.map(t => ({ id: t.id, sessions: t.currentSession, unlocked: t.unlocked })));
  };

  // Render connection lines between dependent talents
  const renderConnections = () => {
    const connections: JSX.Element[] = [];
    
    talents.forEach(talent => {
      if (talent.requires) {
        // Handle both simple array (AND) and array of arrays (OR groups)
        if (Array.isArray(talent.requires[0])) {
          // OR condition: draw lines to all prerequisites in all groups
          (talent.requires as string[][]).forEach(group => {
            group.forEach(reqId => {
              const reqTalent = talents.find(t => t.id === reqId);
              if (reqTalent) {
                const startX = reqTalent.col * GRID_SIZE + gridDimensions.offsetX + NODE_SIZE / 2;
                const startY = reqTalent.row * GRID_SIZE + gridDimensions.offsetY + NODE_SIZE / 2;
                const endX = talent.col * GRID_SIZE + gridDimensions.offsetX + NODE_SIZE / 2;
                const endY = talent.row * GRID_SIZE + gridDimensions.offsetY + NODE_SIZE / 2;
                
                connections.push(
                  <line
                    key={`${reqId}-${talent.id}`}
                    x1={startX}
                    y1={startY}
                    x2={endX}
                    y2={endY}
                    stroke={talent.category === 'longevity' ? '#9c27b0' : 
                           talent.category === 'healthspan' ? '#00bcd4' : '#4caf50'}
                    strokeWidth={2}
                    strokeOpacity={0.6}
                    strokeDasharray={talent.currentSession > 0 ? 'none' : '5,5'}
                  />
                );
              }
            });
          });
        } else {
          // AND condition: draw lines to all prerequisites
          (talent.requires as string[]).forEach(reqId => {
            const reqTalent = talents.find(t => t.id === reqId);
            if (reqTalent) {
              const startX = reqTalent.col * GRID_SIZE + gridDimensions.offsetX + NODE_SIZE / 2;
              const startY = reqTalent.row * GRID_SIZE + gridDimensions.offsetY + NODE_SIZE / 2;
              const endX = talent.col * GRID_SIZE + gridDimensions.offsetX + NODE_SIZE / 2;
              const endY = talent.row * GRID_SIZE + gridDimensions.offsetY + NODE_SIZE / 2;
              
              connections.push(
                <line
                  key={`${reqId}-${talent.id}`}
                  x1={startX}
                  y1={startY}
                  x2={endX}
                  y2={endY}
                  stroke={talent.category === 'longevity' ? '#9c27b0' : 
                         talent.category === 'healthspan' ? '#00bcd4' : '#4caf50'}
                  strokeWidth={3}
                  strokeOpacity={0.8}
                  strokeDasharray={talent.currentSession > 0 ? 'none' : '5,5'}
                />
              );
            }
          });
        }
      }
    });
    
    return (
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1
        }}
      >
        {connections}
      </svg>
    );
  };

  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: '100vh', 
        position: 'relative',
        // Tree background image with reduced opacity for better readability
        backgroundImage: 'linear-gradient(to top, rgba(139, 69, 19, 0.1) 0%, transparent 30%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/tree-background.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
          backgroundRepeat: 'no-repeat',
          opacity: 0.3,
          zIndex: -1
        }
      }}
    >
      {/* Toggle Button for Control Panel - Only when panel is hidden */}
      {!showControlPanel && (
        <Paper
          elevation={2}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 25,
            p: 1,
            backgroundColor: themeMode === 'dark' 
              ? 'rgba(30, 30, 30, 0.95)' 
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            borderRadius: 1
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowControlPanel(true)}
            sx={{
              minWidth: 'auto',
              p: 0.5,
              color: themeMode === 'dark' ? '#ffffff' : 'inherit',
              borderColor: themeMode === 'dark' ? '#666' : 'inherit'
            }}
          >
            <Menu />
          </Button>
        </Paper>
      )}

      {/* Compact Control Panel */}
      {showControlPanel && (
        <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 20,
          p: 2,
          backgroundColor: themeMode === 'dark' 
            ? 'rgba(30, 30, 30, 0.95)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          borderRadius: 2,
          maxWidth: 250,
          width: 'auto',
          color: themeMode === 'dark' ? '#ffffff' : 'inherit'
        }}
      >
        {/* Title with Close Button */}
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Health Tree
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => setShowControlPanel(false)}
            sx={{
              minWidth: 'auto',
              p: 0.5,
              color: themeMode === 'dark' ? '#ffffff' : 'inherit'
            }}
          >
            <MenuOpen />
          </Button>
        </Box>
        
        {/* Health Statistics */}
        <Box sx={{ 
          mb: 1.5, 
          p: 1, 
          backgroundColor: themeMode === 'dark' 
            ? 'rgba(76, 175, 80, 0.2)' 
            : 'rgba(76, 175, 80, 0.1)', 
          borderRadius: 1, 
          border: `1px solid rgba(76, 175, 80, ${themeMode === 'dark' ? '0.5' : '0.3'})` 
        }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#4caf50', display: 'block', mb: 0.5 }}>
            🏆 Impact
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              +{healthStats.totalYearsGained} years of life gained
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUp sx={{ fontSize: '0.8rem', color: '#4caf50' }} />
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                ${healthStats.healthWorthIncrease.toLocaleString()} Networth
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {/* Cost Statistics */}
        <Box sx={{ 
          mb: 1.5, 
          p: 1, 
          backgroundColor: themeMode === 'dark' 
            ? 'rgba(255, 152, 0, 0.2)' 
            : 'rgba(255, 152, 0, 0.1)', 
          borderRadius: 1, 
          border: `1px solid rgba(255, 152, 0, ${themeMode === 'dark' ? '0.5' : '0.3'})` 
        }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#ff9800', display: 'block', mb: 0.5 }}>
            ⏱️ Cost
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              {healthStats.totalCostYears} years spent on health
            </Typography>
          </Box>
        </Box>

        
        {/* Hours Selection and Recommendations */}
        <Box sx={{ 
          mb: 1.5, 
          p: 1, 
          backgroundColor: themeMode === 'dark' 
            ? 'rgba(63, 81, 181, 0.2)' 
            : 'rgba(63, 81, 181, 0.1)', 
          borderRadius: 1, 
          border: `1px solid rgba(63, 81, 181, ${themeMode === 'dark' ? '0.5' : '0.3'})` 
        }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#3f51b5', display: 'block', mb: 1 }}>
            ⏰ Weekly Hours
          </Typography>
          <Box sx={{ px: 1, mb: 1 }}>
            <Slider
              value={weeklyHours}
              onChange={(_, newValue) => setWeeklyHours(newValue as number)}
              min={1}
              max={25}
              step={0.5}
              marks={[
                { value: 1, label: '1h' },
                { value: 5, label: '5h' },
                { value: 10, label: '10h' },
                { value: 15, label: '15h' },
                { value: 20, label: '20h' },
                { value: 25, label: '25h' }
              ]}
              size="small"
              sx={{ color: '#3f51b5' }}
            />
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 'bold', textAlign: 'center', display: 'block', mb: 1 }}>
            {weeklyHours} hours/week
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleGetRecommendations}
              sx={{ 
                minWidth: 'auto',
                px: 2,
                py: 0.5,
                fontSize: '0.7rem',
                backgroundColor: '#3f51b5',
                '&:hover': { backgroundColor: '#303f9f' }
              }}
            >
              Get Recommendations
            </Button>
          </Box>
        </Box>

        {/* Current Weekly Commitment */}
        <Box sx={{ 
          mb: 1.5, 
          p: 1, 
          backgroundColor: themeMode === 'dark' 
            ? 'rgba(156, 39, 176, 0.2)' 
            : 'rgba(156, 39, 176, 0.1)', 
          borderRadius: 1, 
          border: `1px solid rgba(156, 39, 176, ${themeMode === 'dark' ? '0.5' : '0.3'})` 
        }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#9c27b0', display: 'block', mb: 0.5 }}>
            📅 Current Commitment
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              {Math.floor(currentCommitmentMinutes / 60)}h {currentCommitmentMinutes % 60}m/week
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
              ({currentCommitmentMinutes} minutes)
            </Typography>
          </Box>
        </Box>

        {/* Weekly Commitment Button */}
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={handleLockInClick}
            disabled={getLockInButtonState().disabled}
            startIcon={getLockInButtonState().icon}
            sx={{
              backgroundColor: activeLocking ? '#10b981' : '#3f51b5',
              '&:hover': {
                backgroundColor: activeLocking ? '#059669' : '#303f9f',
              },
              '&:disabled': {
                backgroundColor: '#d1d5db',
                color: '#9ca3af',
              },
              fontSize: '0.75rem',
              py: 1,
              px: 2,
              borderRadius: 2,
            }}
          >
            {getLockInButtonState().text}
          </Button>
        </Box>

        {/* Category Legend */}
        <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
          {Object.entries(TREE_CATEGORIES).map(([key, category]) => (
            <Chip
              key={key}
              label={category.name}
              size="small"
              sx={{
                backgroundColor: category.color,
                color: 'white',
                fontSize: '0.65rem',
                height: 18,
                '& .MuiChip-icon': { color: 'white', fontSize: '0.7rem' },
                '& .MuiChip-label': { px: 0.5 }
              }}
              icon={category.icon}
            />
          ))}
        </Box>

        {/* Reset Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setResetDialogOpen(true)}
            color="warning"
            sx={{ 
              minWidth: 'auto',
              px: 1,
              py: 0.5,
              fontSize: '0.7rem'
            }}
          >
            <RestartAlt sx={{ fontSize: '1rem', mr: 0.5 }} />
            Reset
          </Button>
        </Box>
      </Paper>
      )}

      {/* Recommendations Panel */}
      {showRecommendations && recommendations.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            zIndex: 20,
            p: 2,
            maxWidth: 400,
            backgroundColor: themeMode === 'dark' 
              ? 'rgba(30, 30, 30, 0.95)' 
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            borderRadius: 2,
            color: themeMode === 'dark' ? '#ffffff' : 'inherit'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Recommendations
            </Typography>
            <Button
              size="small"
              onClick={() => setShowRecommendations(false)}
              sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
            >
              ✕
            </Button>
          </Box>
          
          {recommendations.slice(0, 5).map(rec => (
            <Box key={rec.talent.id} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                label={`#${rec.talent.ranking}`}
                color={rec.reason === 'high-priority' ? 'primary' : 'default'}
              />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {rec.talent.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {rec.totalMinutes}min
              </Typography>
            </Box>
          ))}
        </Paper>
      )}

      {/* Zoom/Pan Container with Talent Tree */}
      <ZoomPanContainer
        minZoom={0.8}
        maxZoom={1.1}
        contentDimensions={{ width: gridDimensions.width, height: gridDimensions.height }}
      >
        <Box
          sx={{
            width: gridDimensions.width,
            height: gridDimensions.height,
            position: 'relative',
            minHeight: '100vh'
          }}
        >
          {/* Connection Lines */}
          {renderConnections()}
          
          {/* Talent Nodes */}
          {talents.map(talent => {
            const isAvailable = isTalentAvailable(talent);
            const isRecommended = recommendations.some(r => r.talent.id === talent.id);
            const isLocked = !isPremium && talent.id !== 'health-tree' && !FREE_USER_TALENTS.includes(talent.id);
            
            return (
              <Tooltip
                key={talent.id}
                title={
                  <Box sx={{ maxWidth: 300 }}>
                    {isLocked ? (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#ff9800', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Lock sx={{ fontSize: 16 }} />
                          Locked Talent
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, color: '#ff9800' }}>
                          Unavailable for free users.{' '}
                          <a 
                            href="/pricing" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#ff9800', textDecoration: 'underline' }}
                          >
                            Upgrade to Premium to unlock
                          </a>
                          .
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: talent.color }}>
                          {talent.name}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.4 }}>
                          {talent.description}
                        </Typography>
                        
                        {/* Talent Statistics */}
                        <Box sx={{ mb: 1, p: 1, backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: 1 }}>
                          <Typography variant="caption" display="block">
                            ⏱️ Time: {talent.timeRequired}min/session
                          </Typography>
                          <Typography variant="caption" display="block">
                            🎯 Progress: {talent.currentSession}/{talent.maxSession} sessions
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                }
                placement="top"
                arrow
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: talent.col * GRID_SIZE + gridDimensions.offsetX,
                    top: talent.row * GRID_SIZE + gridDimensions.offsetY,
                    zIndex: 10
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <Badge
                      badgeContent={`${talent.currentSession}/${talent.maxSession}`}
                      color="secondary"
                      overlap="circular"
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      sx={{
                        '& .MuiBadge-badge': {
                          zIndex: 20,
                          backgroundColor: '#000000',
                          color: '#ffffff',
                          fontWeight: 'bold',
                          borderRadius: '4px',
                          padding: '2px 4px',
                          fontSize: '0.7rem',
                          minWidth: 'auto',
                          height: 'auto',
                        }
                      }}
                    >
                    {talent.id === 'health-tree' ? (
                      <TalentNodeButton
                        $unlocked={talent.unlocked}
                        $hasSessions={talent.currentSession > 0}
                        $selected={false}
                        $treeColor={talent.color}
                        $available={isAvailable}
                        $isDark={themeMode === 'dark'}
                        $locked={isLocked}
                        onClick={(e: React.MouseEvent) => handleTalentClick(talent, e)}
                        onContextMenu={(e: React.MouseEvent) => {
                          e.preventDefault();
                          handleTalentClick(talent, e);
                        }}
                        disabled={!isAvailable || isLocked}
                      >
                        <img 
                          src="/Health-Tree.svg" 
                          alt="Health Tree Logo" 
                          style={getImageStyle(isLocked)} 
                        />
                      </TalentNodeButton>
                    ) : talent.id === 'functional-mobility' ? (
                      <TalentNodeButton
                        $unlocked={talent.unlocked}
                        $hasSessions={talent.currentSession > 0}
                        $selected={false}
                        $treeColor={talent.color}
                        $available={isAvailable}
                        $isDark={themeMode === 'dark'}
                        $locked={isLocked}
                        onClick={(e: React.MouseEvent) => handleTalentClick(talent, e)}
                        onContextMenu={(e: React.MouseEvent) => {
                          e.preventDefault();
                          handleTalentClick(talent, e);
                        }}
                        disabled={!isAvailable || isLocked}
                      >
                        <img 
                          src="/images/FunctionalMobility.png" 
                          alt="Functional Mobility Icon" 
                          style={getImageStyle(isLocked)} 
                        />
                      </TalentNodeButton>
                    ) : (
                      <TalentNodeButton
                        $unlocked={talent.unlocked}
                        $hasSessions={talent.currentSession > 0}
                        $selected={false}
                        $treeColor={talent.color}
                        $available={isAvailable}
                        $isDark={themeMode === 'dark'}
                        $locked={isLocked}
                        onClick={(e: React.MouseEvent) => handleTalentClick(talent, e)}
                        onContextMenu={(e: React.MouseEvent) => {
                          e.preventDefault();
                          handleTalentClick(talent, e);
                        }}
                        disabled={!isAvailable || isLocked}
                      >
                      {talent.id === 'sleep-hygiene'
                            ? <img 
                                src="/images/seep.png" 
                                alt="Sleep Icon" 
                                style={getImageStyle(isLocked)} 
                              />
                            : talent.id === 'oral-health'
                              ? <img 
                                  src="/images/teeth.png" 
                                  alt="Oral Health Icon" 
                                  style={getImageStyle(isLocked)} 
                                />
                              : talent.id === 'sunseeker'
                                ? <img 
                                    src="/images/sun.png" 
                                    alt="Sun Seeker Icon" 
                                    style={getImageStyle(isLocked)} 
                                  />
                                : talent.id === 'stability-mastery'
                                ? <img 
                                    src="/images/stability2.png" 
                                    alt="Stability Mastery Icon" 
                                    style={getImageStyle(isLocked)} 
                                  />
                                : talent.id === 'zone-two-cardio'
                                  ? <img 
                                      src="/images/zone2.png" 
                                      alt="Zone Two Cardio Icon" 
                                      style={getImageStyle(isLocked)} 
                                    />
                                  : talent.id === 'zone-five-cardio'
                                    ? <img 
                                        src="/images/zone5.png" 
                                        alt="Zone Five Cardio Icon" 
                                        style={getImageStyle(isLocked)} 
                                      />
                                    : talent.id === 'strength-training'
                                      ? <img 
                                          src="/images/strength.png" 
                                          alt="Strength Training Icon" 
                                          style={getImageStyle(isLocked)} 
                                        />
                                      : talent.id === 'recovery-mastery'
                                        ? <img 
                                            src="/images/Recovery.png" 
                                            alt="Recovery Mastery Icon" 
                                            style={getImageStyle(isLocked)} 
                                          />
                                        : talent.id === 'social-connection'
                                          ? <img 
                                              src="/images/social.png" 
                                              alt="Social Connection Icon" 
                                              style={getImageStyle(isLocked)} 
                                            />
                                          : talent.id === 'guided-breathwork'
                                            ? <img 
                                                src="/images/breath.png" 
                                                alt="Guided Breathwork Icon" 
                                                style={getImageStyle(isLocked)} 
                                              />
                                            : talent.id === 'elastic-edge'
                                              ? <img 
                                                  src="/images/Elastic.png" 
                                                  alt="Elastic Edge Icon" 
                                                  style={getImageStyle(isLocked)} 
                                                />
                                              : talent.id === 'energy-balance'
                                                ? <img 
                                                    src="/images/energy_balance.png" 
                                                    alt="Energy Balance Icon" 
                                                    style={getImageStyle(isLocked)} 
                                                  />
                                                : talent.id === 'creatine-supplementation'
                                                  ? <img 
                                                      src="/images/creatine.png" 
                                                      alt="Creatine Supplementation Icon" 
                                                      style={getImageStyle(isLocked)} 
                                                    />
                                                  : talent.id === 'purpose-meaning'
                                                    ? <img 
                                                        src="/images/purpose.png" 
                                                        alt="Purpose & Meaning Icon" 
                                                        style={getImageStyle(isLocked)} 
                                                      />
                                                    : talent.id === 'emotional-regulation'
                                                      ? <img 
                                                          src="/images/emotional_regulation.png" 
                                                          alt="Emotional Regulation Icon" 
                                                          style={getImageStyle(isLocked)} 
                                                        />
                                                      : talent.id === 'fiber-intake'
                                                        ? <img 
                                                            src="/images/fiber.png" 
                                                            alt="Fiber Intake Icon" 
                                                            style={getImageStyle(isLocked)} 
                                                          />
                                                        : talent.id === 'time-restricted-eating'
                                                          ? <img 
                                                              src="/images/time.png" 
                                                              alt="Time Restricted Eating Icon" 
                                                              style={getImageStyle(isLocked)} 
                                                            />
                                                          : talent.id === 'protein-optimization'
                                                            ? <img 
                                                                src="/images/Protein.png" 
                                                                alt="Protein Optimization Icon" 
                                                                style={getImageStyle(isLocked)} 
                                                              />
                                                            : talent.id === 'stability-mastery'
                                                              ? <img 
                                                                  src="/images/stability.png" 
                                                                  alt="Stability Mastery Icon" 
                                                                  style={getImageStyle(isLocked)} 
                                                                />
                                                              : talent.icon}
                      {isLocked && (
                        <Lock
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            fontSize: '24px',
                            color: '#fff',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            borderRadius: '50%',
                            padding: '4px',
                            zIndex: 10
                          }}
                        />
                      )}
                      {isRecommended && (
                        <CheckCircle
                          sx={{
                            position: 'absolute',
                            top: -5,
                            left: -5,
                            fontSize: '20px',
                            color: '#4caf50',
                            backgroundColor: 'white',
                            borderRadius: '50%'
                          }}
                        />
                      )}
                    </TalentNodeButton>
                    )}
                    
                    {/* Lock icon overlay for locked talents */}
                    {isLocked && (
                      <Lock
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '28px',
                          color: '#ff9800',
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          borderRadius: '50%',
                          padding: '6px',
                          zIndex: 25
                        }}
                      />
                    )}
                  </Badge>
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </ZoomPanContainer>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Health Tree?</DialogTitle>
        <DialogContent>
          <Typography>
            This will reset all your talent progress back to the beginning. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResetConfirm} color="warning" variant="contained">
            Reset All Progress
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lock-in Confirmation Dialog */}
      <Dialog open={lockInConfirmationOpen} onClose={() => setLockInConfirmationOpen(false)}>
        <DialogTitle>Lock in Talents for the Week?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            You are about to lock in your current talent progress for this week. This action is <strong>final and irreversible</strong> until Sunday at midnight when the new week begins.
          </Typography>
          
          <Typography variant="h6" sx={{ mb: 1 }}>Talents to be locked in:</Typography>
          {talents.filter(t => t.currentSession > 0 && t.id !== 'health-tree').map(talent => (
            <Typography key={talent.id} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
              • {talent.name} ({talent.currentSession} sessions, {talent.currentSession * talent.timeRequired} min/week)
            </Typography>
          ))}
          
          <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
            Total weekly commitment: {talents.filter(t => t.currentSession > 0 && t.id !== 'health-tree').reduce((total, t) => total + (t.currentSession * t.timeRequired), 0)} minutes
          </Typography>
          
          <Typography sx={{ mt: 2, color: 'warning.main' }}>
            ⚠️ Once locked in, you cannot modify your talent selection until next week.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockInConfirmationOpen(false)}>Cancel</Button>
          <Button onClick={confirmLockIn} color="primary" variant="contained">
            Lock in Talents
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnifiedTalentTreeView;